import React from 'react';
import { Alert, Button, Descriptions, Input, Modal, Space, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { KccAccountCredentialDTO } from '../api/kfc';

interface Props {
  open: boolean;
  title?: string;
  credential: KccAccountCredentialDTO | null;
  onClose: () => void;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 回退到 execCommand
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

const KccAccountCredentialModal: React.FC<Props> = ({ open, title = 'KCC 账号信息', credential, onClose }) => {
  const copy = async (text: string) => {
    if (await copyText(text)) message.success('已复制');
    else message.error('复制失败，请手动选择复制');
  };

  const copyAll = () => {
    if (!credential) return;
    copy(
      `登录地址：${credential.portalUrl}\n登录邮箱：${credential.loginEmail}\n密码：${credential.password}`
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Space>
          <Button icon={<CopyOutlined />} onClick={copyAll} disabled={!credential}>
            复制全部
          </Button>
          <Button type="primary" onClick={onClose}>
            关闭
          </Button>
        </Space>
      }
    >
      {credential && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="请通过安全渠道将账号信息发送给客户，不要在公开群聊中传播密码。"
          />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="登录地址">
              <Typography.Text copyable={{ text: credential.portalUrl }}>{credential.portalUrl}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="登录邮箱">
              <Typography.Text copyable={{ text: credential.loginEmail }}>{credential.loginEmail}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="密码">
              <Space.Compact style={{ width: '100%' }}>
                <Input.Password readOnly value={credential.password} />
                <Button icon={<CopyOutlined />} onClick={() => copy(credential.password)}>
                  复制
                </Button>
              </Space.Compact>
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
};

export default KccAccountCredentialModal;
